import React, { useEffect, useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import { BatteryCharging, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";
import { shapeBatteryLog, batteryTicks, fmtUptime } from "utils/batteryLog";

/**
 * Battery % over time for one wearable (its own flash log, firmware v0.17.0),
 * shared by the USB device screen and the case dashboard.
 *
 *   - background bands by state: on battery / charging / charged
 *   - a marked gap where the device was off (the boot number changed)
 *   - real time on the x axis when the device knew the time; boots where it
 *     never did are placed in order and labelled "boot N +Xh", never given
 *     a clock time (see shapeBatteryLog)
 *   - hover: time, %, mV, state
 *
 * readings: [{ epoch, boot, up, mv, pct, state }] oldest first.
 */

const THEMES = {
  light: {
    line: "#E07A3A",
    grid: "#F3F4F6",
    axis: "#9CA3AF",
    off: "#9CA3AF",
    tooltipBg: "#FFFFFF",
    tooltipBorder: "#E5E7EB",
    tooltipText: "#111827",
    tooltipMuted: "#6B7280",
    bandOpacity: 0.14,
    offOpacity: 0.05,
  },
  dark: {
    line: "#F59E5B",
    grid: "#1F2937",
    axis: "#6B7280",
    off: "#6B7280",
    tooltipBg: "#111827",
    tooltipBorder: "#374151",
    tooltipText: "#F9FAFB",
    tooltipMuted: "#9CA3AF",
    bandOpacity: 0.22,
    offOpacity: 0.1,
  },
};

// Band colours by state. On battery is quiet: it's the normal case.
export const BATTERY_STATE_COLORS = {
  "on-batt": "#94A3B8",
  charging: "#F59E0B",
  charged: "#10B981",
};
const STATE_KEYS = {
  "on-batt": "onBattery",
  charging: "charging",
  charged: "charged",
};

/** Follows the app's `dark` class on <html> (DarkModeContext). */
function useIsDark() {
  const read = () =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const [dark, setDark] = useState(read);
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);
  return dark;
}

const fmtClock = (epochS, withDate) =>
  new Date(epochS * 1000).toLocaleString(
    dateLocale("en-GB"),
    withDate
      ? {
          weekday: "short",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        }
      : { hour: "2-digit", minute: "2-digit" }
  );

const LegendSwatch = ({ color, hatch, dashed, label }) => (
  <span className="flex items-center gap-1.5">
    <span
      className="inline-block h-3 w-3 rounded-sm"
      style={
        hatch
          ? {
              backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 5px)`,
              border: `1px solid ${color}`,
            }
          : dashed
          ? { border: `1px dashed ${color}` }
          : { backgroundColor: color, opacity: 0.55 }
      }
    />
    {label}
  </span>
);

const BatteryTooltip = ({ active, payload, theme, mode }) => {
  const { t } = useTranslation();
  const p = active && payload?.[0]?.payload;
  if (!p || p.pct == null) return null;
  const when =
    p.epoch > 0
      ? fmtClock(p.epoch, true)
      : t("devices.batteryChart.bootOffset", {
          boot: p.boot,
          offset: fmtUptime(p.up),
        });
  return (
    <div
      className="rounded-lg border px-3 py-2 text-[12px] shadow-sm"
      style={{
        background: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        color: theme.tooltipText,
      }}
    >
      <p className="font-semibold">{when}</p>
      {p.epoch > 0 && (
        <p style={{ color: theme.tooltipMuted }}>
          {t("devices.batteryChart.bootOffset", {
            boot: p.boot,
            offset: fmtUptime(p.up),
          })}
        </p>
      )}
      {p.epoch === 0 && mode === "time" && (
        <p style={{ color: theme.tooltipMuted }}>
          {t("devices.batteryChart.clockNotSet")}
        </p>
      )}
      <p className="mt-1 tabular-nums">
        <span className="font-semibold">{p.pct}%</span>
        {p.mv != null && (
          <span style={{ color: theme.tooltipMuted }}>{` · ${p.mv} mV`}</span>
        )}
      </p>
      <p className="flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: BATTERY_STATE_COLORS[p.state] }}
        />
        {t(`devices.batteryChart.state.${STATE_KEYS[p.state]}`)}
      </p>
    </div>
  );
};

const BatteryHistoryChart = ({
  readings,
  intervalS = 60,
  loading = false,
  loadingText,
  emptyText,
  height = 260,
}) => {
  const { t } = useTranslation();
  const dark = useIsDark();
  const theme = dark ? THEMES.dark : THEMES.light;
  const shaped = useMemo(
    () => shapeBatteryLog(readings || [], { intervalS }),
    [readings, intervalS]
  );
  const ticks = useMemo(() => batteryTicks(shaped), [shaped]);

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#E5E7EB] text-[13px] text-[#6B7280] dark:border-[#374151] dark:text-[#9CA3AF]"
        style={{ height }}
        role="status"
      >
        <Loader2 size={18} className="animate-spin text-[#E07A3A]" />
        {loadingText || t("devices.batteryChart.loading")}
      </div>
    );
  }

  if (!shaped.points.length) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#E5E7EB] px-6 text-center dark:border-[#374151]"
        style={{ height }}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F3F4F6] dark:bg-[#1F2937]">
          <BatteryCharging size={18} className="text-[#9CA3AF]" />
        </div>
        <p className="text-[13px] text-[#6B7280] dark:text-[#9CA3AF]">
          {emptyText || t("devices.batteryChart.empty")}
        </p>
      </div>
    );
  }

  const [lo] = shaped.domain;
  const multiDay =
    shaped.mode === "time" &&
    new Date(shaped.domain[0] * 1000).toDateString() !==
      new Date(shaped.domain[1] * 1000).toDateString();
  const tickFormatter = (x) =>
    shaped.mode === "time"
      ? fmtClock(x, multiDay && ticks.length && x === ticks[0])
      : fmtUptime(x - lo);
  const anyEstimated = shaped.boots.some((b) => b.estimated);
  const latest = shaped.latest;

  return (
    <div className="space-y-2">
      <div style={{ height }} data-testid="battery-history-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={shaped.points}
            margin={{ top: 18, right: 12, bottom: 4, left: -12 }}
          >
            <CartesianGrid stroke={theme.grid} vertical={false} />
            {shaped.bands.map((b, i) => (
              <ReferenceArea
                key={`band-${i}`}
                x1={b.x1}
                x2={b.x2}
                y1={0}
                y2={100}
                fill={BATTERY_STATE_COLORS[b.state]}
                fillOpacity={theme.bandOpacity}
                stroke={b.estimated ? BATTERY_STATE_COLORS[b.state] : "none"}
                strokeDasharray={b.estimated ? "3 3" : undefined}
                strokeOpacity={0.5}
                ifOverflow="hidden"
              />
            ))}
            {shaped.offs.map((o, i) => (
              <ReferenceArea
                key={`off-${i}`}
                x1={o.x1}
                x2={o.x2}
                y1={0}
                y2={100}
                fill={theme.off}
                fillOpacity={theme.offOpacity}
                stroke={theme.off}
                strokeOpacity={0.35}
                strokeDasharray="2 4"
                ifOverflow="hidden"
                label={{
                  value: t("devices.batteryChart.off"),
                  position: "insideTop",
                  fill: theme.axis,
                  fontSize: 10,
                }}
              />
            ))}
            <XAxis
              dataKey="x"
              type="number"
              domain={shaped.domain}
              ticks={ticks}
              tickFormatter={tickFormatter}
              tick={{ fontSize: 11, fill: theme.axis }}
              tickLine={false}
              axisLine={{ stroke: theme.grid }}
              allowDataOverflow
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 11, fill: theme.axis }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              content={<BatteryTooltip theme={theme} mode={shaped.mode} />}
              cursor={{ stroke: theme.axis, strokeDasharray: "3 3" }}
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="pct"
              stroke={theme.line}
              strokeWidth={1.75}
              dot={false}
              activeDot={{ r: 3 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-[#6B7280] dark:text-[#9CA3AF]">
        {["on-batt", "charging", "charged"].map((s) => (
          <LegendSwatch
            key={s}
            color={BATTERY_STATE_COLORS[s]}
            label={t(`devices.batteryChart.state.${STATE_KEYS[s]}`)}
          />
        ))}
        <LegendSwatch
          hatch
          color={theme.off}
          label={t("devices.batteryChart.off")}
        />
        {anyEstimated && (
          <LegendSwatch
            dashed
            color={theme.axis}
            label={t("devices.batteryChart.clockNotSet")}
          />
        )}
        {latest && (
          <span className="ml-auto tabular-nums">
            {t("devices.batteryChart.latest", {
              pct: latest.pct,
              count: shaped.boots.length,
            })}
          </span>
        )}
      </div>
      {shaped.mode === "sequence" ? (
        <p className="text-[11px] text-[#9CA3AF]">
          {t("devices.batteryChart.sequenceNote")}
        </p>
      ) : anyEstimated ? (
        <p className="text-[11px] text-[#9CA3AF]">
          {t("devices.batteryChart.estimatedNote")}
        </p>
      ) : null}
    </div>
  );
};

export default BatteryHistoryChart;
